// ABOUTME: TanStack Query hooks over the outreach routes the CRM uses: the leads' drafts in
// play (one-tap tasks), marking one and stopping contact with a prospect.
// Every key starts with 'prospecting', so a PROSPECT_* / OUTREACH_* notification on the event
// stream refreshes them; drafts Outreach saves are polled by the page showing them.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './client'
import type { OutreachTask, TaskMarkStatus } from '@/types/prospecting'

export const PROSPECTING_KEY = 'prospecting'
const keys = {
  tasks: [PROSPECTING_KEY, 'tasks'] as const,
}

// Outreach writes drafts in the background; the CRM picks them up this often.
export const DRAFTS_POLL_MS = 20_000
const DRAFTS_LIMIT = 500

/** The human-readable part of an API error. An outreach refusal `{error, code}` arrives as
 * `details` of the error envelope (its message is the bare code), or as the message itself
 * from a mocked route. */
export function apiErrorText(error: unknown): string {
  const reason = (error as { details?: { error?: unknown } } | null)?.details?.error
  if (typeof reason === 'string') return reason
  const message = (error as { message?: unknown } | null)?.message
  if (typeof message === 'string') return message
  const nested = (message as { error?: unknown } | null)?.error
  return typeof nested === 'string' ? nested : 'Something went wrong'
}

function useInvalidateProspecting() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [PROSPECTING_KEY] })
}

/** The leads' drafts still in play (ready, or sent awaiting a reply), newest first. Each
 * observer runs its own timer, so only one per page passes `pollMs`. */
export function useOutreachTasks(pollMs: number | false = false) {
  return useQuery({
    queryKey: keys.tasks,
    queryFn: () => api.get<OutreachTask[]>(`/outreach/tasks?drafts=true&limit=${DRAFTS_LIMIT}`),
    refetchInterval: pollMs,
  })
}

/** The owner's rewrite of a lead's ready-to-send message (POST /outreach/drafts). */
export function useSaveDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (task: OutreachTask & { prospect_id: string }) =>
      api.post<OutreachTask>('/outreach/drafts', {
        prospect_id: task.prospect_id,
        channel: task.channel,
        body: task.script,
        subject: task.subject,
        evidence_ids: task.evidence_ids,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PROSPECTING_KEY] }),
  })
}

export function useMarkTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: TaskMarkStatus }) =>
      api.post<OutreachTask>(`/outreach/tasks/${taskId}/mark`, { status }),
    onSuccess: (task) => {
      queryClient.setQueryData<OutreachTask[]>(keys.tasks, (tasks) =>
        tasks?.map((t) => (t.id === task.id ? task : t))
      )
      return queryClient.invalidateQueries({ queryKey: [PROSPECTING_KEY] })
    },
  })
}

/** Stop contacting a prospect for good: every way of reaching it, its open drafts skipped. */
export function useStopProspect() {
  const invalidate = useInvalidateProspecting()
  return useMutation({
    mutationFn: (prospectId: string) =>
      api.post<{ suppressed: string[] }>('/outreach/suppressions', { prospect_id: prospectId }),
    onSuccess: invalidate,
  })
}
